import type { PlanEdge, PlanNode, PlanRole } from './types';

/**
 * What this person may do to one topic. The server enforces every rule here;
 * these only decide which controls to draw. Plan state (locked, archived) is
 * the caller's to add: a locked plan refuses every write whatever the role.
 */

/** Owner or plan admin: every node, plus order, accept and reject. */
export const canManage = (role: PlanRole | null | undefined): boolean => role === 'owner' || role === 'admin';

/** Full rights on this one node: text, color, position, arrows out of it, removal. */
export const canEditNode = (
  node: Pick<PlanNode, 'authority_id'>,
  role: PlanRole | null | undefined,
  userId: string | null | undefined,
): boolean => {
  if (!role || role === 'viewer') return false;
  if (canManage(role)) return true;
  return !!userId && node.authority_id === userId;
};

/** Title and notes only: everyone above, plus the member who proposed a still-proposed node. */
export const canEditNodeText = (
  node: Pick<PlanNode, 'authority_id' | 'status' | 'proposed_by'>,
  role: PlanRole | null | undefined,
  userId: string | null | undefined,
): boolean =>
  canEditNode(node, role, userId) ||
  (!!role && role !== 'viewer' && !!userId && node.status === 'proposed' && node.proposed_by === userId);

/** An arrow may go when its source is a node this person holds. */
export const canArrowFrom = canEditNode;

/** An arrow may be removed by a manager, or by the authority on either end. */
export const canRemoveEdge = (
  edge: Pick<PlanEdge, 'source_id' | 'target_id'>,
  nodesById: Map<string, Pick<PlanNode, 'authority_id'>>,
  role: PlanRole | null | undefined,
  userId: string | null | undefined,
): boolean => {
  if (canManage(role)) return true;
  const s = nodesById.get(edge.source_id);
  const t = nodesById.get(edge.target_id);
  return (!!s && canEditNode(s, role, userId)) || (!!t && canEditNode(t, role, userId));
};
